import { TypeVisionDocument } from './type-vision-document';

export interface I18nString {
  [key: string]: string;
}

export type FileType =
  | 'doc' | 'docx' | 'docm' | 'DOC' | 'DOCX' | 'DOCM'
  | 'xls' | 'xlt' | 'xml' | 'xlsx' | 'xlsm' | 'xlsb' | 'xltx' | 'xltm'
  | 'XLS' | 'XLT' | 'XML' | 'XLSX' | 'XLSM' | 'XLSB' | 'XLTX' | 'XLTM'
  | 'pdf' | 'PDF'
  | 'png' | 'PNG'
  | 'jpg' | 'jpeg' | 'JPG' | 'JPEG'
  | 'ppt' | 'pptx' | 'thmx' | 'ppsx' | 'pps' | 'ppsm'
  | 'PPT' | 'PPTX' | 'THMX' | 'PPSX' | 'PPS' | 'PPSM'
  | 'mp4' | 'wmv' | '3gp' | 'avi' | 'mp3' | 'wav'
  | 'MP4' | 'WMV' | '3GP' | 'AVI' | 'MP3' | 'WAV'
  | string;

export type ViewMode = 'grid' | 'list';

export interface IDocument {
  uid: string;
  name: I18nString;
  type: FileType;
  url: string;
  icon?: string;
  size?: number;
  createdAt?: any;
  updatedAt?: any;
  total_access?: number;
  tags?: string[];
}

export interface IFolder {
  uid: string;
  name: I18nString;
  description?: I18nString;
  folderCoverImg?: string;
  typeVision: TypeVisionDocument;
  groups?: Record<string, boolean>;
  order?: number;
  orderDocuments?: string;
  createdAt?: any;
  documentCount?: number;
}

export interface IBreadcrumbItem {
  label: string;
  path: string;
}

export enum FileCategory {
  Word = 'Word',
  Excel = 'Excel',
  PowerPoint = 'PowerPoint',
  PDF = 'PDF',
  Image = 'Image',
  Media = 'Media',
  Generic = 'Generic File',
}

export const FILE_TYPE_MAP: Record<string, string> = {
  doc: 'assets/icon/Files/doc_ico.svg',
  docx: 'assets/icon/Files/doc_ico.svg',
  docm: 'assets/icon/Files/doc_ico.svg',
  DOC: 'assets/icon/Files/doc_ico.svg',
  DOCX: 'assets/icon/Files/doc_ico.svg',
  DOCM: 'assets/icon/Files/doc_ico.svg',
  xls: 'assets/icon/Files/excel_ico.svg',
  xlt: 'assets/icon/Files/excel_ico.svg',
  xlsx: 'assets/icon/Files/excel_ico.svg',
  xlsm: 'assets/icon/Files/excel_ico.svg',
  xlsb: 'assets/icon/Files/excel_ico.svg',
  xltx: 'assets/icon/Files/excel_ico.svg',
  xltm: 'assets/icon/Files/excel_ico.svg',
  xml: 'assets/icon/Files/excel_ico.svg',
  XLS: 'assets/icon/Files/excel_ico.svg',
  XLT: 'assets/icon/Files/excel_ico.svg',
  XLSX: 'assets/icon/Files/excel_ico.svg',
  XLSM: 'assets/icon/Files/excel_ico.svg',
  XLSB: 'assets/icon/Files/excel_ico.svg',
  XLTX: 'assets/icon/Files/excel_ico.svg',
  XLTM: 'assets/icon/Files/excel_ico.svg',
  XML: 'assets/icon/Files/excel_ico.svg',
  pdf: 'assets/icon/Files/pdf_ico.svg',
  PDF: 'assets/icon/Files/pdf_ico.svg',
  png: 'assets/icon/Files/png_ico.svg',
  PNG: 'assets/icon/Files/png_ico.svg',
  jpg: 'assets/icon/Files/jpg_ico.svg',
  JPG: 'assets/icon/Files/jpg_ico.svg',
  jpeg: 'assets/icon/Files/jpg_ico.svg',
  JPEG: 'assets/icon/Files/jpg_ico.svg',
  ppt: 'assets/icon/Files/ppt_ico.svg',
  pptx: 'assets/icon/Files/ppt_ico.svg',
  thmx: 'assets/icon/Files/ppt_ico.svg',
  ppsx: 'assets/icon/Files/ppt_ico.svg',
  pps: 'assets/icon/Files/ppt_ico.svg',
  ppsm: 'assets/icon/Files/ppt_ico.svg',
  PPT: 'assets/icon/Files/ppt_ico.svg',
  PPTX: 'assets/icon/Files/ppt_ico.svg',
  THMX: 'assets/icon/Files/ppt_ico.svg',
  PPSX: 'assets/icon/Files/ppt_ico.svg',
  PPS: 'assets/icon/Files/ppt_ico.svg',
  PPSM: 'assets/icon/Files/ppt_ico.svg',
  mp4: 'assets/icon/Files/media_ico.svg',
  wmv: 'assets/icon/Files/media_ico.svg',
  '3gp': 'assets/icon/Files/media_ico.svg',
  avi: 'assets/icon/Files/media_ico.svg',
  mp3: 'assets/icon/Files/audio_ico.svg',
  wav: 'assets/icon/Files/audio_ico.svg',
  MP4: 'assets/icon/Files/media_ico.svg',
  WMV: 'assets/icon/Files/media_ico.svg',
  '3GP': 'assets/icon/Files/media_ico.svg',
  AVI: 'assets/icon/Files/media_ico.svg',
  MP3: 'assets/icon/Files/audio_ico.svg',
  WAV: 'assets/icon/Files/audio_ico.svg',
};

export function getFileIcon(type: string): string {
  return FILE_TYPE_MAP[type] || 'assets/icon/Files/generic-file_ico.svg';
}

export function getFileCategory(type: string): FileCategory {
  const lower = type.toLowerCase();
  if (['doc', 'docx', 'docm'].includes(lower)) return FileCategory.Word;
  if (['xls', 'xlt', 'xml', 'xlsx', 'xlsm', 'xlsb', 'xltx', 'xltm'].includes(lower)) return FileCategory.Excel;
  if (['ppt', 'pptx', 'thmx', 'ppsx', 'pps', 'ppsm'].includes(lower)) return FileCategory.PowerPoint;
  if (['pdf'].includes(lower)) return FileCategory.PDF;
  if (['png', 'jpg', 'jpeg'].includes(lower)) return FileCategory.Image;
  if (['mp4', 'wmv', '3gp', 'avi', 'mp3', 'wav'].includes(lower)) return FileCategory.Media;
  return FileCategory.Generic;
}
